import React, {
  CSSProperties,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import cl from 'classnames';
import { useLanguage, useContextData } from 'src/hooks';
import { usePaginator } from 'src/components/general';
import {
  TableMarkers,
  StickyTableType,
  STSortOption,
} from './stickyTable.interface';
import styles from './stickyTableStyle.module.scss';

type ColumnOptions = Record<
  string,
  { index: number; left: number; isSticky: boolean }
>;

type Header = {
  key: string;
  node: ReactNode;
  sortKey: string;
  hasSorter: boolean;
};

type Cell = {
  key: string;
  node: ReactNode;
  expandLabel?: string;
  rowId?: string | number;
};

const ExpandLabel: React.FC<{
  label: string;
  expanded: boolean;
  onClick: () => void;
}> = ({ label, expanded, onClick }) => {
  return (
    <div className={styles.expandLabelButton} onClick={onClick}>
      <div className={styles.expandLabel}>{label}</div>
      <svg
        width="14"
        height="16"
        viewBox="0 0 14 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={cl(styles.arrowIcon, expanded && styles.expanded)}
      >
        <path
          d="M2.91536 10.5923L6.9987 6.05525L11.082 10.5923"
          stroke="#323C46"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
};

const TableRow: React.FC<{
  cells: Cell[];
  columnOptions: ColumnOptions;
  gridRowIndex: number;
  markers?: TableMarkers;
}> = ({ cells, columnOptions, gridRowIndex, markers }) => {
  const [expanded, setExpanded] = useState<{
    key: string;
    node: ReactNode;
  } | null>(null);
  const [bottom, setBottom] = useState(false);

  const [isL1Marked, isL2Marked] = useMemo(() => {
    if (!markers || cells[0]?.rowId === undefined) {
      return [false, false];
    }

    return [
      markers.l1.includes(cells[0].rowId),
      markers.l2.includes(cells[0].rowId),
    ];
  }, [cells, markers]);

  useEffect(() => {
    if (expanded) {
      setBottom(true);
      return;
    }

    setTimeout(() => {
      setBottom(false);
    }, 220);
  }, [expanded]);

  return (
    <>
      {cells.map(({ key, node, expandLabel }, index) => {
        const option = columnOptions[key];
        const style: CSSProperties = {
          gridColumn: `${option.index} / ${option.index + 1}`,
          gridRow: `${gridRowIndex} / ${gridRowIndex + 1}`,
        };

        if (isL1Marked) {
          style.backgroundColor = '#F2F9FF';
        }

        if (isL2Marked) {
          style.backgroundColor = '#E6F3FF';
        }

        if (option.isSticky) {
          style.left = option.left;
          style.zIndex = 3;
          style.backgroundColor = '#FCFCFC';
          style.borderRight = '1px solid #EDF2F8';

          if (isL1Marked) {
            style.backgroundColor = '#E9F5FF';
          }

          if (isL2Marked) {
            style.backgroundColor = '#DEEFFF';
          }
        }

        return (
          <div
            className={cl(
              styles.cell,
              option.isSticky && styles.sticky,
              bottom && styles.bottom,
            )}
            style={style}
            key={`t-cell-${gridRowIndex}-${index}`}
          >
            {expandLabel ? (
              <div className={styles.expandedDesktopCell}>
                <ExpandLabel
                  label={expandLabel}
                  expanded={expanded?.key === key}
                  onClick={() => {
                    setExpanded((prevState) => {
                      if (!prevState || prevState.key !== key) {
                        return {
                          key,
                          node,
                        };
                      }

                      return null;
                    });
                  }}
                />
                <div
                  className={cl(
                    styles.expandedContainer,
                    styles.desktop,
                    expanded && styles.expanded,
                  )}
                >
                  <div style={{ minHeight: 0, minWidth: 0 }}>
                    {expanded?.node}
                  </div>
                </div>
              </div>
            ) : (
              node
            )}
          </div>
        );
      })}
      <div
        style={{
          gridColumn: `1 / ${cells.length + 1}`,
          gridRow: `${gridRowIndex + 1} / ${gridRowIndex + 2}`,
        }}
        className={styles.expandedMobileCell}
      >
        <div
          className={cl(
            styles.expandedContainer,
            styles.mobile,
            expanded && styles.expanded,
          )}
        >
          <div style={{ minHeight: 0 }}>{expanded?.node}</div>
        </div>
      </div>
    </>
  );
};

const TableSummaryRow: React.FC<{
  cells: Cell[];
  columnOptions: ColumnOptions;
  gridRowIndex: number;
  top: number;
}> = ({ cells, columnOptions, gridRowIndex, top }) => {
  return (
    <>
      {cells.map(({ key, node }, index) => {
        const option = columnOptions[key];
        const style: CSSProperties = {
          gridColumn: `${option.index} / ${option.index + 1}`,
          gridRow: `${gridRowIndex} / ${gridRowIndex + 1}`,
          fontWeight: 600,
          zIndex: option.isSticky ? 5 : 4,
          top,
        };

        if (option.isSticky) {
          style.left = option.left;
          style.backgroundColor = '#FCFCFC';
          style.borderRight = '1px solid #EDF2F8';
        }

        return (
          <div
            className={cl(styles.cell, styles.sticky)}
            style={style}
            key={`t-cell-${gridRowIndex}-${index}`}
          >
            {node}
          </div>
        );
      })}
    </>
  );
};

const HiddenTable: React.FC<{
  headers: Header[];
  data: Cell[][];
  noDataCell: ReactNode | null;
  withSummary: boolean;
  onUpdate: (
    data: Cell[][],
    noDataCell: ReactNode | null,
    widthMap: Map<number, number>,
    insetsMap: Map<number, number>,
    tableHeaderHeight: number,
  ) => void;
}> = ({ headers, data, noDataCell, withSummary, onUpdate }) => {
  const widthRef = useRef<Map<number, number>>(new Map());
  const tableHeaderHeightRef = useRef<number>(0);
  const columnsCount = useMemo(() => headers.length, [headers.length]);

  useEffect(() => {
    const insetsMap = new Map<number, number>();
    insetsMap.set(0, 0);
    let inset = 0;
    for (let index = 1; index < widthRef.current.size; index += 1) {
      inset += widthRef.current.get(index - 1) || 0;
      insetsMap.set(index, inset);
    }

    onUpdate(
      data,
      noDataCell,
      widthRef.current,
      insetsMap,
      tableHeaderHeightRef.current,
    );
    widthRef.current = new Map();
  }, [onUpdate, data, noDataCell]);

  return (
    <div className={styles.hiddenWrapper}>
      <div
        className={styles.table}
        style={{ gridTemplateColumns: `repeat(${columnsCount}, auto)` }}
      >
        {headers.map(({ node }, index) => (
          <div
            className={styles.headerCell}
            ref={(node) => {
              const rect = node?.getBoundingClientRect();
              if (rect) {
                widthRef.current.set(index, rect.width);

                if (index === headers.length - 1) {
                  tableHeaderHeightRef.current = rect.height;
                }
              }
            }}
            key={`ht-header-${index}`}
          >
            <div className={styles.header}>{node}</div>
          </div>
        ))}
        {data.map((cells, rowIndex) =>
          cells.map(({ node, expandLabel }, index) => {
            const style: CSSProperties = {};

            if (withSummary && rowIndex === 0) {
              style.fontWeight = 600;
            }

            return (
              <div
                className={styles.cell}
                style={style}
                key={`ht-cell-${rowIndex}-${index}`}
              >
                {expandLabel ? (
                  <ExpandLabel
                    label={expandLabel}
                    expanded={false}
                    onClick={() => {}}
                  />
                ) : (
                  node
                )}
              </div>
            );
          }),
        )}
        {noDataCell !== null && (
          <div style={{ gridColumn: `1 / ${columnsCount + 1}` }}>
            {noDataCell}
          </div>
        )}
      </div>
    </div>
  );
};

const emptySet: Set<string> = new Set();

export const StickyTable: StickyTableType = ({
  config,
  data,
  withPaginator = false,
  summaryRecord,
  isLoading,
  stickyCols = emptySet,
  className,
  autoWidth = false,
  onSort,
  customSorter,
  markers,
  countsPerPages = [20, 40, 60],
  initPerPage = 20,
  initCurrentPage = 1,
  skipPaginatorInit,
  isFiltered = false,
  resetFilter,
  notFoundText,
  onPaginatorChange,
  emptyCell,
  rowKey,
}) => {
  const {
    header: { height: headerHeight, width: headerWidth },
    screen: { isLaptop },
  } = useContextData();
  const { t } = useLanguage();
  const [hiddenData, setHiddenData] = useState<Cell[][] | null>(null);
  const [hiddenNoData, setHiddenNoData] = useState<ReactNode | null>(
    <div className={styles.emptyCell} />,
  );
  const [handledData, setHandledData] = useState<Cell[][] | null>(null);
  const [handledNoData, setHandledNoData] = useState<ReactNode | null>(
    hiddenNoData,
  );
  const [columnOptions, setColumnOptions] = useState<ColumnOptions>({});
  const [sortOption, setSortOption] = useState<STSortOption | null>(null);
  const columnsCount = useMemo(() => config?.length ?? 0, [config?.length]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const { currentPage, countPerPage, paginator } = usePaginator({
    countsPerPages,
    initPerPage,
    initCurrentPage,
    skipInit: skipPaginatorInit,
    dataLength: data.length,
    onChange: () => {
      scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    className: styles.paginator,
  });
  const stickyColsRef = useRef<Set<string>>(stickyCols);
  const tableHeaderHeightRef = useRef<number>(0);
  const insetsRef = useRef<Map<number, number>>(new Map());
  const widthMapRef = useRef<Map<number, number>>(new Map());

  useEffect(() => {
    onSort?.(sortOption);
  }, [onSort, sortOption]);

  useEffect(() => {
    onPaginatorChange?.(currentPage, countPerPage);
  }, [currentPage, countPerPage, onPaginatorChange]);

  const headers = useMemo(
    () =>
      config?.map(({ key, title, sorter }) => ({
        key,
        sortKey: key,
        hasSorter: Boolean(sorter),
        node: (
          <>
            <div className={styles.label}>{title}</div>
            {sorter && (
              <div className={styles.sort}>
                <svg
                  width="10"
                  height="6"
                  viewBox="0 0 10 6"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={styles.sortAsc}
                >
                  <path
                    d="M5 0L9.33013 5.25L0.669873 5.25L5 0Z"
                    fill={
                      !sortOption ||
                      sortOption.key !== key ||
                      sortOption.sortOrder === 'ascend'
                        ? '#7B8691'
                        : '#1890ff'
                    }
                  />
                </svg>
                <svg
                  width="10"
                  height="6"
                  viewBox="0 0 10 6"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={styles.sortDesc}
                >
                  <path
                    d="M5 6L0.669872 0.75L9.33013 0.75L5 6Z"
                    fill={
                      !sortOption ||
                      sortOption.key !== key ||
                      sortOption.sortOrder === 'descend'
                        ? '#7B8691'
                        : '#1890ff'
                    }
                  />
                </svg>
              </div>
            )}
          </>
        ),
      })) ?? [],
    [config, sortOption],
  );

  useEffect(() => {
    if (!config) {
      return;
    }

    if (isLoading === null) {
      setHiddenData([]);
      return;
    }

    if (isLoading) {
      return;
    }

    const startIndex = (currentPage - 1) * countPerPage;
    const endIndex = currentPage * countPerPage;

    const sortFunction = sortOption
      ? config.find(({ key }) => sortOption.key === key)?.sorter
      : null;

    const sortedData = [...data].sort((a, b) => {
      const primary = customSorter?.(a, b, sortOption) ?? 0;
      if (primary !== 0) {
        return primary;
      }

      if (!sortOption || !sortFunction) {
        return 0;
      }

      return sortFunction(a, b, sortOption.sortOrder);
    });

    const pagedData = withPaginator
      ? sortedData.slice(startIndex, endIndex)
      : sortedData;

    const preparedData: Cell[][] = pagedData.map((record) =>
      config.map(({ key, field, render, expandLabel }, index) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        const value = record[field];
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        const rowId = rowKey ? (record[rowKey] as string | number) : undefined;
        return {
          key,
          node: render?.(value, record, index) || value,
          expandLabel,
          rowId,
        };
      }),
    );

    if (summaryRecord && pagedData.length) {
      preparedData.unshift(
        config.map(({ key, field, summaryRender, render }, index) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          const value = summaryRecord[field];
          return {
            key,
            node:
              summaryRender?.(value, summaryRecord, index) ||
              render?.(value, summaryRecord, index) ||
              value,
          };
        }),
      );
    }

    setHiddenData(preparedData);

    if (!preparedData.length) {
      if (isFiltered) {
        setHiddenNoData(
          resetFilter && notFoundText ? (
            <div className={styles.resetFilterCell}>
              <div>{t('general.notFound')}</div>
              <div className={styles.resetFilterText} onClick={resetFilter}>
                {notFoundText}
              </div>
            </div>
          ) : (
            <div className={styles.emptyCell}>{t('general.noData')}</div>
          ),
        );
      } else {
        setHiddenNoData(
          <div className={styles.emptyCell}>
            {emptyCell ?? t('general.noData')}
          </div>,
        );
      }
    } else {
      setHiddenNoData(null);
    }
  }, [
    config,
    data,
    summaryRecord,
    sortOption,
    customSorter,
    currentPage,
    countPerPage,
    isLaptop,
    isLoading,
    isFiltered,
    resetFilter,
    notFoundText,
    withPaginator,
    emptyCell,
    rowKey,
    t,
  ]);

  const calcColOptions = useCallback(() => {
    if (!config) {
      return;
    }

    let colIndex = stickyColsRef.current.size + 1;
    let stickyColIndex = 1;
    let left = 0;

    setColumnOptions(
      config.reduce((result, { key }, index) => {
        const options = stickyColsRef.current.has(key)
          ? {
              index: stickyColIndex,
              left,
              isSticky: true,
            }
          : {
              index: colIndex,
              left: 0,
              isSticky: false,
            };

        if (stickyColsRef.current.has(key)) {
          stickyColIndex += 1;
          left += widthMapRef.current.get(index) || 0;
        } else {
          colIndex += 1;
        }

        return { ...result, [key]: options };
      }, {} as ColumnOptions),
    );
  }, [config]);

  useEffect(() => {
    const runCalc = Boolean(stickyColsRef.current);

    stickyColsRef.current = stickyCols;

    if (runCalc) {
      calcColOptions();
    }
  }, [calcColOptions, stickyCols]);

  const onUpdate = useCallback(
    (
      data: Cell[][],
      noDataCell: ReactNode | null,
      widthMap: Map<number, number>,
      insetsMap: Map<number, number>,
      tableHeaderHeight: number,
    ) => {
      widthMapRef.current = widthMap;
      insetsRef.current = insetsMap;
      tableHeaderHeightRef.current = tableHeaderHeight;

      calcColOptions();
      setHandledData(data);
      setHandledNoData(noDataCell);
    },
    [calcColOptions],
  );

  return (
    <div
      className={cl(styles.container, className)}
      style={{
        minWidth: autoWidth ? 'auto' : headerWidth,
      }}
    >
      <div className={styles.hiddenScroll} ref={scrollRef}></div>
      <div className={styles.wrapper}>
        {hiddenData && (
          <HiddenTable
            headers={headers}
            data={hiddenData}
            noDataCell={hiddenNoData}
            withSummary={Boolean(summaryRecord)}
            onUpdate={onUpdate}
          />
        )}
        {config && (
          <div
            className={styles.table}
            style={{ gridTemplateColumns: `repeat(${columnsCount}, auto)` }}
          >
            {headers.map(({ key, node, sortKey, hasSorter }, index) => {
              const option = columnOptions[key];

              const optionIndex = option?.index ?? index + 1;

              const style: CSSProperties = {
                top: headerHeight,
                gridColumn: `${optionIndex} / ${optionIndex + 1}`,
                gridRow: '1 / 2',
              };

              if (option?.isSticky) {
                style.left = option.left;
                style.zIndex = 5;
                style.backgroundColor = '#FCFCFC';
                style.borderRight = '1px solid #EDF2F8';
              }

              return (
                <div
                  className={cl(styles.headerCell, styles.sticky)}
                  style={style}
                  onClick={() => {
                    if (!hasSorter || !option) {
                      return;
                    }

                    setSortOption((prev) => {
                      if (prev === null || prev.key !== sortKey) {
                        return {
                          key: sortKey,
                          sortOrder: 'ascend',
                        };
                      }

                      return prev.sortOrder === 'ascend'
                        ? { ...prev, sortOrder: 'descend' }
                        : null;
                    });
                  }}
                  key={`t-header-${index}`}
                >
                  <div className={styles.header}>{node}</div>
                </div>
              );
            })}
            {handledData?.map((cells, rowIndex) =>
              summaryRecord && rowIndex === 0 ? (
                <TableSummaryRow
                  key={`t-row-${rowIndex}`}
                  cells={cells}
                  columnOptions={columnOptions}
                  gridRowIndex={rowIndex * 2 + 2}
                  top={tableHeaderHeightRef.current + headerHeight}
                />
              ) : (
                <TableRow
                  key={`t-row-${rowIndex}`}
                  cells={cells}
                  columnOptions={columnOptions}
                  gridRowIndex={rowIndex * 2 + 2}
                  markers={markers}
                />
              ),
            )}
            {handledData && handledNoData !== null && (
              <div style={{ gridColumn: `1 / ${columnsCount + 1}` }}>
                {handledNoData}
              </div>
            )}
          </div>
        )}
        {isLoading && (
          <div className={styles.loading}>
            <div className={styles.loader} />
          </div>
        )}
      </div>
      {withPaginator && (
        <div className={styles.paginatorContainer}>{paginator}</div>
      )}
    </div>
  );
};

export default StickyTable;
